import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { LogOut, Inbox } from 'lucide-react';
import NotificationBell from '../NotificationBell';

/**
 * VendorHeaderBar — top hero strip on /vendor/dashboard.
 *
 * Mobile (<sm): the layout becomes a two-row stack so a long company name
 * (e.g. "Sri Sai Core Traders") no longer wraps to one-word-per-line beside
 * the logo, and the action buttons no longer overflow the viewport.
 *   row 1: [logo] [name + ID badge + status meta]
 *   row 2: [Messages]  [Logout]  (full-width, flex)
 *
 * Desktop (≥sm): single horizontal row — original layout.
 */
const VendorHeaderBar = ({ vendor, onLogout }) => (
  <section className="bg-gradient-to-r from-[#2c54b2] to-[#1e3a8a] text-white">
    <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {vendor.logo_url ? (
          <img src={vendor.logo_url} alt="" className="w-12 h-12 sm:w-14 sm:h-14 rounded-md object-cover bg-white border border-white/20 shrink-0" />
        ) : (
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-md bg-white text-[#2c54b2] flex items-center justify-center font-bold shrink-0">{vendor.logo}</div>
        )}
        <div className="min-w-0 flex-1">
          <h1 className="text-base sm:text-xl font-bold leading-tight truncate" data-testid="vendor-header-name">{vendor.name}</h1>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
            {vendor.vendor_code && (
              <span className="inline-block text-[10px] sm:text-[11px] font-mono uppercase tracking-wider bg-white/10 text-white border border-white/20 rounded px-1.5 py-0.5 whitespace-nowrap" data-testid="vendor-code-badge">
                ID: {vendor.vendor_code}
              </span>
            )}
            <span className="text-[11px] sm:text-xs text-blue-100">
              {vendor.city}{vendor.city ? ' • ' : ''}
              Status: <span className={vendor.status === 'active' ? 'text-green-300' : 'text-amber-300'}>{vendor.status}</span>
              {vendor.plan_name && <> • Plan: {vendor.plan_name}</>}
            </span>
          </div>
        </div>
      </div>
      <div className="flex gap-2 sm:gap-2 sm:shrink-0 items-center">
        <NotificationBell role="vendor" tone="light" />
        <Link to="/vendor/messages" data-testid="vendor-messages-link" className="flex-1 sm:flex-initial">
          <Button variant="outline" className="w-full sm:w-auto bg-white/10 hover:bg-white/20 border-white/30 text-white">
            <Inbox className="w-4 h-4 mr-2" /> Messages
          </Button>
        </Link>
        <Button onClick={onLogout} variant="outline" className="flex-1 sm:flex-initial bg-white/10 hover:bg-white/20 border-white/30 text-white" data-testid="vendor-logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </Button>
      </div>
    </div>
  </section>
);

export default VendorHeaderBar;
